import type { Rider, RiderProfileUpdateData, RiderKYCDetailsUpdateData } from '../../services/riderService';

type EditableField = keyof (RiderProfileUpdateData & RiderKYCDetailsUpdateData);
type Section = { title: string; kyc?: boolean; readOnlyFields?: EditableField[]; fields: [EditableField, string][] };
export const riderEditSections: Record<string, Section> = {
  personal: { title: 'Personal Information', readOnlyFields: ['clientRiderId', 'clientName'], fields: [['name', 'Full Name'], ['dob', 'Date of Birth'], ['blood_group', 'Blood Group'], ['clientRiderId', 'Client Rider ID'], ['clientName', 'Client Name']] },
  contact: { title: 'Contact Information', fields: [['phone', 'Phone Number'], ['email', 'Email']] },
  address: { title: 'Address Information', fields: [['address1', 'Address Line 1'], ['address2', 'Address Line 2'], ['city', 'City'], ['state', 'State'], ['pincode', 'PIN Code']] },
  emergency: { title: 'Emergency Contact', fields: [['emergencyName', 'Name'], ['emergencyPhone', 'Phone'], ['emergencyRelation', 'Relation']] },
  aadhaar: { title: 'Aadhaar Number', kyc: true, fields: [['aadharNumber', 'Aadhaar Number']] },
  pan: { title: 'PAN Number', kyc: true, fields: [['panNumber', 'PAN Number']] },
  licence: { title: 'Driving Licence Number', kyc: true, fields: [['drivingLicenseNumber', 'Driving Licence Number']] },
};
export type RiderEditSection = keyof typeof riderEditSections;

export function getRiderSectionValues(rider: Rider, section: RiderEditSection): Record<string, string> {
  return Object.fromEntries(riderEditSections[section].fields.map(([field]) => {
    let value = rider[field] || '';
    if (field === 'dob' && value) {
      // Preserve the calendar date of ISO values, without timezone conversion.
      value = /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : '';
    }
    return [field, value];
  }));
}

export function getRiderSectionChanges(section: RiderEditSection, initial: Record<string, string>, form: Record<string, string>): RiderProfileUpdateData & RiderKYCDetailsUpdateData {
  return Object.fromEntries(riderEditSections[section].fields.flatMap(([field]) => {
    if (riderEditSections[section].readOnlyFields?.includes(field)) return [];
    const value = (form[field] || '').trim();
    return value === (initial[field] || '').trim() ? [] : [[field, value || null]];
  }));
}

export function validateRiderSectionChanges(data: RiderProfileUpdateData & RiderKYCDetailsUpdateData): string {
  if ('name' in data && (!data.name || data.name.length < 2)) return 'Full name must contain at least 2 characters.';
  if ('phone' in data && !/^[6-9]\d{9}$/.test(data.phone || '')) return 'Enter a valid 10-digit mobile number starting with 6, 7, 8 or 9.';
  if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) return 'Enter a valid email address.';
  if (data.emergencyPhone && !/^[6-9]\d{9}$/.test(data.emergencyPhone)) return 'Enter a valid 10-digit emergency mobile number.';
  if (data.pincode && !/^[1-9]\d{5}$/.test(data.pincode)) return 'Enter a valid 6-digit PIN code.';
  if (data.aadharNumber && !/^\d{12}$/.test(data.aadharNumber)) return 'Aadhaar must contain exactly 12 digits.';
  if (data.panNumber && !/^[A-Z]{5}\d{4}[A-Z]$/.test(data.panNumber)) return 'Enter a valid PAN number (for example ABCDE1234F).';
  if (data.drivingLicenseNumber && !/^[A-Z0-9 -]{8,20}$/.test(data.drivingLicenseNumber)) return 'Enter a valid driving licence number (8–20 characters).';
  if (data.dob) {
    const date = new Date(data.dob);
    const adultDate = new Date();
    adultDate.setFullYear(adultDate.getFullYear() - 18);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(data.dob) || Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== data.dob || date > adultDate) return 'Enter a valid date of birth; the rider must be at least 18.';
  }
  return '';
}
